import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Users, Zap, Target, Scale, ShieldCheck, Mail } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const Home = () => {

  const scrollToHash = (section) => {
    document.getElementById(section)?.scrollIntoView({behavior: 'smooth'})
  };
  const promises = [
    {
      icon: <Scale size={24} />,
      title: "Integrity",
      description: "Honesty and transparency in all actions."
    },
    {
      icon: <Target size={24} />,
      title: "Customer Focus",
      description: "Your vision is our mission."
    },
    {
      icon: <Users size={24} />,
      title: "Collaboration",
      description: "Strong client relationships."
    },
    {
      icon: <ShieldCheck size={24} />,
      title: "Quality",
      description: "Excellence in every service."
    }
  ];

  const services = [
    "3D Modeling (Architecture, Structure, MEP - LOD 100-500)",
    "Clash Detection & Coordination",
    "Quantity and Material Take-offs", 
    "4D & 5D BIM",
    "Scan to BIM",
    "Revit Family Creation",
    "CAD to BIM Conversion",
    "MEP BIM Services",
    "VDC (Virtual Design and Construction)",
    "Rendering Images and Video",
  ];

  const recentWins = [
    "Cut payment delays significantly",
    "Standardised key processes in days (not months)",
    "Gave teams a clear, shared view of work"
  ];

  return (
    <div className="min-h-screen bg-bg-page">
      <Header />
      
      {/* Hero Section */}
      <section className="hero-section" id="home">
        <div className="hero-background">
          <img 
            src="/assets/hero-banner.png"
            alt="Professional business consulting"
            className="hero-image"
          />
          <div className="hero-overlay"></div>
        </div>
        
        <div className="container relative z-10">
          <div className="hero-content">
            <h1  className="animate__animated animate__slideInRight brand-display mb-6">
              Victorical White Horses <br />Pvt. Ltd.
            </h1>
            <p className="body-large mb-8 text-text-secondary hero-sub-heading animate__animated animate__slideInRight">
              From Vision To Victory, Software That Wins
            </p>
            <div className="sm:flex-row gap-4">
              <button
                onClick={() => scrollToHash('contact-us')}
                className={`btn-primary`}
              >
                {"Let's Connect"}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* About Us */}
      <section className="py-20 bg-bg-card" id="about">
        <div className="container text-center">
          <div className="max-w-4xl m-auto">
            <h2 className="heading-2 mb-8 text-center">About Us</h2>
            <p className="body-medium text-text-secondary leading-relaxed">
              Victorical White Horses Software Service Industries Pvt. Ltd. is a forward-thinking and dynamic organization committed to delivering excellence in the field of Building Information Modeling (BIM). Established with a vision to revolutionize the construction and infrastructure industry through digital innovation, we bring together technology, expertise, and creativity to help our clients build smarter and faster.
            </p>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 bg-bg-card section-bg-gradient">
        <div className="container text-center">
          <h2 className="text-white text-5xl font-bold mb-8">Services</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-2 lg:gap-12">
            <div className="text-center">
              <span className="flex items-start space-x-3 my-2">
                <CheckCircle className="text-brand-primary mt-1 flex-shrink-0" size={20} />
                <span className="textwhite text-lg">3D Modeling (Architecture, Structure, MEP - LOD 100-500)</span>
              </span>
              <span className="flex items-start space-x-3 my-2">
                <CheckCircle className="text-brand-primary mt-1 flex-shrink-0" size={20} />
                <span className="textwhite text-lg">Clash Detection & Coordination</span>
              </span>
              <span className="flex items-start space-x-3 my-2">
                <CheckCircle className="text-brand-primary mt-1 flex-shrink-0" size={20} />
                <span className="textwhite text-lg">Quantity and Material Take-offs</span>
              </span>
              <span className="flex items-start space-x-3 my-2">
                <CheckCircle className="text-brand-primary mt-1 flex-shrink-0" size={20} />
                <span className="textwhite text-lg">4D & 5D BIM</span>
              </span>
              <span className="flex items-start space-x-3 my-2">
                <CheckCircle className="text-brand-primary mt-1 flex-shrink-0" size={20} />
                <span className="textwhite text-lg">Scan to BIM</span>
              </span>
            </div>
            <div>
              <span className="flex items-start space-x-3 my-2">
                <CheckCircle className="text-brand-primary mt-1 flex-shrink-0" size={20} />
                <span className="textwhite text-lg">Revit Family Creation</span>
              </span>
              <span className="flex items-start space-x-3 my-2">
                <CheckCircle className="text-brand-primary mt-1 flex-shrink-0" size={20} />
                <span className="textwhite text-lg">CAD to BIM Conversion</span>
              </span>
              <span className="flex items-start space-x-3 my-2">
                <CheckCircle className="text-brand-primary mt-1 flex-shrink-0" size={20} />
                <span className="textwhite text-lg">MEP Design Services</span>
              </span>
              <span className="flex items-start space-x-3 my-2">
                <CheckCircle className="text-brand-primary mt-1 flex-shrink-0" size={20} />
                <span className="textwhite text-lg">VDC (Virtual Design and Construction)</span>
              </span>
              <span className="flex items-start space-x-3 my-2">
                <CheckCircle className="text-brand-primary mt-1 flex-shrink-0" size={20} />
                <span className="textwhite text-lg">Rendering Images and Video</span>
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20">
        <div className="container">
          <h2 className="heading-2 mb-12 text-center">Values</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {promises.map((promise, index) => (
              <div key={index} className="team-card p-8">
                <div className="text-brand-primary mb-4">
                  {promise.icon}
                </div>
                <h3 className="heading-5 mb-4 text-text-primary">
                  {promise.title}
                </h3>
                <p className="body-small text-text-secondary">
                  {promise.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Technology Stack */}
      <section className="py-20 bg-white">
        <div className="container">
          <h2 className="heading-2 mb-12 text-center">Our Technology Stack</h2>
          <div className="grid grid-cols-1 gap-8">
            <div
                x-data="{}"
                x-init="$nextTick(() => {
                    let ul = $refs.logos;
                    ul.insertAdjacentHTML('afterend', ul.outerHTML);
                    ul.nextSibling.setAttribute('aria-hidden', 'true');
                })"
                className="w-full inline-flex flex-nowrap overflow-hidden [mask-image:_linear-gradient(to_right,transparent_0,_black_128px,_black_calc(100%-128px),transparent_100%)]"
            >
                <ul x-ref="logos" className="flex items-center justify-center md:justify-start [&_li]:mx-8 [&_img]:max-w-none animate-infinite-scroll">
                    <li>
                        <img src="/assets/softwares/bluebeam.webp" width={200} className="mix-blend-multiply" alt="bluebeam" />
                    </li>
                    <li>
                        <img src="/assets/softwares/twinmotion.webp" width={200} className="mix-blend-multiply" alt="twinmotion" />
                    </li>
                    <li>
                        <img src="/assets/softwares/lumion.webp" width={200} className="mix-blend-multiply" alt="lumion" />
                    </li>
                    <li>
                        <img src="/assets/softwares/enscape.webp" width={200} className="mix-blend-multiply" alt="enscape" />
                    </li>
                    <li>
                        <img src="/assets/softwares/autocad.webp" width={200} className="mix-blend-multiply" alt="autocad" />
                    </li>
                    <li>
                        <img src="/assets/softwares/recap.webp" width={200} className="mix-blend-multiply" alt="recap" />
                    </li>
                    <li>
                        <img src="/assets/softwares/revit.webp" width={200} className="mix-blend-multiply" alt="revit" />
                    </li>
                    <li>
                        <img src="/assets/softwares/naviswork.webp" width={200} className="mix-blend-multiply" alt="naviswork" />
                    </li>
                </ul>
                <ul x-ref="logos" className="flex items-center justify-center md:justify-start [&_li]:mx-8 [&_img]:max-w-none animate-infinite-scroll" area-hidden="true">
                    <li>
                        <img src="/assets/softwares/bluebeam.webp" width={200} className="mix-blend-multiply" alt="bluebeam" />
                    </li>
                    <li>
                        <img src="/assets/softwares/twinmotion.webp" width={200} className="mix-blend-multiply" alt="twinmotion" />
                    </li>
                    <li>
                        <img src="/assets/softwares/lumion.webp" width={200} className="mix-blend-multiply" alt="lumion" />
                    </li>
                    <li>
                        <img src="/assets/softwares/enscape.webp" width={200} className="mix-blend-multiply" alt="enscape" />
                    </li>
                    <li>
                        <img src="/assets/softwares/autocad.webp" width={200} className="mix-blend-multiply" alt="autocad" />
                    </li>
                    <li>
                        <img src="/assets/softwares/recap.webp" width={200} className="mix-blend-multiply" alt="recap" />
                    </li>
                    <li>
                        <img src="/assets/softwares/revit.webp" width={200} className="mix-blend-multiply" alt="revit" />
                    </li>
                    <li>
                        <img src="/assets/softwares/naviswork.webp" width={200} className="mix-blend-multiply" alt="naviswork" />
                    </li>
                </ul>                
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 section-bg-gradient" id="contact-us">
        <div className="pilot-offer animate__animated animate__heartBeat animate__infinite animate__slower offer-text pb-10">
          <span className='text-xl font-bold'>
            Free Pilot Project
          </span>
          <br/>
          <span className='text-lg'>
            The First Step Towards Partnership.
          </span>
        </div>
        <div className="container text-center">
          <h2 className="text-5xl font-bold mb-6 text-white">Let’s talk about your project</h2>
          <p className="mb-8 text-white/90 max-w-2xl mx-auto text-lg">
            Whether you need a single BIM deliverable or a long-term VDC partner, tell us about your project and we’ll respond within 24 hours.
          </p>
          {/* <Link to="/contact-us" className="btn-primary bg-white text-secondary-trust border-white hover:bg-white/90">
            Contact
            <ArrowRight className="ml-2" size={16} />
          </Link> */}
          <a
              href="mailto:sales@whitehorses.co.in"
              className="btn-primary ml-4"
            >
              <Mail className="text-brand-primary mt-1 flex-shrink-0" size={20} /> &nbsp; sales@whitehorses.co.in
            </a>
        </div>
      </section>

      <section className="calendly-bg">
        <h2 className='text-center text-4xl font-bold mb-6'>Want to learn more? Schedule time with our team now</h2>
        <div className="calendly-inline-widget" data-url="https://calendly.com/ganeshjogdand1406/30min"></div>
        <script type="text/javascript" src="https://assets.calendly.com/assets/external/widget.js" async></script>
      </section>

      <Footer />
    </div>
  );
};

export default Home;